import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Download, Printer, ChevronDown, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts'
import { reportOreTecniciApi, tecniciApi } from '../services/api'

const MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre']

interface Intervento {
  rapportino_id: number; ora: string; numero_pratica: string; frazionario: string
  comune: string; tipologia: string; esito: string; minuti_dal_precedente: number | null
}
interface Giorno {
  data: string; giorno: string; n_interventi: number; primo: string; ultimo: string
  ore: number; interventi: Intervento[]
}
interface TecnicoReport {
  tecnico_id: number; tecnico: string; giorni_lavorati: number; totale_interventi: number
  totale_ore: number; media_ore_giorno: number; giorni: Giorno[]
}

const COLORI = ['#1E40AF', '#F97316', '#0d9488', '#7c3aed', '#15803d', '#dc2626', '#f59e0b']

interface RigaSettimana { label: string; giorniLav: number; previste: number; fatte: number; diff: number }

// Ore previste = ore contrattuali settimanali / 5 per ogni giorno lavorativo (lun-ven) della settimana
// che ricade nel mese; le settimane a cavallo di due mesi sono quindi calcolate in proporzione.
function calcolaSettimane(t: TecnicoReport, anno: number, mese: number, oreSett: number): RigaSettimana[] {
  const map = new Map<string, { inizio: Date; fine: Date; giorniLav: number; fatte: number }>()
  const n = new Date(anno, mese, 0).getDate()
  for (let d = 1; d <= n; d++) {
    const dt = new Date(anno, mese - 1, d)
    const dow = (dt.getDay() + 6) % 7 // 0 = lunedì
    const lun = new Date(anno, mese - 1, d - dow)
    const key = `${lun.getFullYear()}-${lun.getMonth()}-${lun.getDate()}`
    const w = map.get(key) ?? { inizio: dt, fine: dt, giorniLav: 0, fatte: 0 }
    w.fine = dt
    if (dow < 5) w.giorniLav += 1
    const iso = `${anno}-${String(mese).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    w.fatte += t.giorni.find(g => g.data === iso)?.ore ?? 0
    map.set(key, w)
  }
  const f = (x: Date) => `${String(x.getDate()).padStart(2, '0')}/${String(x.getMonth() + 1).padStart(2, '0')}`
  return [...map.values()]
    .filter(w => w.giorniLav > 0 || w.fatte > 0)
    .map(w => {
      const previste = (oreSett / 5) * w.giorniLav
      return { label: `${f(w.inizio)} – ${f(w.fine)}`, giorniLav: w.giorniLav, previste, fatte: w.fatte, diff: w.fatte - previste }
    })
}

const fmtDiff = (h: number) => `${h > 0.0083 ? '+' : h < -0.0083 ? '−' : ''}${fmtOre(Math.abs(h))}`
const coloreDiff = (h: number) => (h > 0.0083 ? '#15803d' : h < -0.0083 ? '#dc2626' : '#64748B')

const fmtData = (iso: string) => iso.split('-').reverse().join('/')
const fmtOre = (h: number) => {
  const m = Math.round(h * 60)
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`
}

export default function ReportOreTecnici() {
  const oggi = new Date()
  const [anno, setAnno] = useState(oggi.getFullYear())
  const [mese, setMese] = useState(oggi.getMonth() + 1)
  const [tecnicoId, setTecnicoId] = useState<number | ''>('')
  const [aperti, setAperti] = useState<Set<string>>(new Set())
  const [exporting, setExporting] = useState(false)
  const [inizioFisso, setInizioFisso] = useState(false)
  const [escludiWeekend, setEscludiWeekend] = useState(false)
  const [oreSett, setOreSett] = useState<number>(() => {
    try { const v = Number(localStorage.getItem('report_ore_contratto')); return v > 0 ? v : 40 } catch { return 40 }
  })
  const impostaOreSett = (v: number) => {
    setOreSett(v)
    try { localStorage.setItem('report_ore_contratto', String(v)) } catch { /* ignora */ }
  }
  const [metrica, setMetrica] = useState<'ore' | 'interventi'>('ore')

  const { data: tecnici } = useQuery({
    queryKey: ['tecnici-select'],
    queryFn: () => tecniciApi.list({ page_size: 200, is_active: true }).then(r => (r.data.items ?? r.data) as { id: number; nome: string; cognome: string }[]),
  })

  const params = { anno, mese, ...(tecnicoId ? { tecnico_id: Number(tecnicoId) } : {}), ...(inizioFisso ? { inizio_fisso: true } : {}), ...(escludiWeekend ? { escludi_weekend: true } : {}) }
  const { data, isLoading } = useQuery({
    queryKey: ['report-ore-tecnici', anno, mese, tecnicoId, inizioFisso, escludiWeekend],
    queryFn: () => reportOreTecniciApi.get(params).then(r => r.data as { tecnici: TecnicoReport[] }),
  })

  const toggle = (k: string) => setAperti(prev => {
    const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n
  })

  const esporta = async () => {
    setExporting(true)
    try {
      const res = await reportOreTecniciApi.exportXlsx(params)
      const url = URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `report_ore_tecnici_${anno}-${String(mese).padStart(2, '0')}${inizioFisso ? '_da0835' : ''}${escludiWeekend ? '_nowe' : ''}.xlsx`
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Errore durante l\'export')
    } finally {
      setExporting(false)
    }
  }

  const anni = Array.from({ length: 4 }, (_, i) => oggi.getFullYear() - i)
  const lista = data?.tecnici ?? []

  const giorniMese = new Date(anno, mese, 0).getDate()
  const datiGrafico = Array.from({ length: giorniMese }, (_, i) => {
    const iso = `${anno}-${String(mese).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`
    const riga: Record<string, number | string> = { giorno: i + 1 }
    lista.forEach(t => {
      const g = t.giorni.find(x => x.data === iso)
      if (g) riga[t.tecnico] = metrica === 'ore' ? g.ore : g.n_interventi
    })
    return riga
  })

  return (
    <div style={{ padding: 20 }}>
      <style>{`@media print { .no-print { display: none !important } .stampa-det { display: table-row !important } }`}</style>
      <div className="no-print" style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <label className="label">Mese</label>
          <select className="select" value={mese} onChange={e => setMese(Number(e.target.value))}>
            {MESI.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Anno</label>
          <select className="select" value={anno} onChange={e => setAnno(Number(e.target.value))}>
            {anni.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Tecnico</label>
          <select className="select" value={tecnicoId} onChange={e => setTecnicoId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Tutti i tecnici</option>
            {(tecnici ?? []).map(t => <option key={t.id} value={t.id}>{t.cognome} {t.nome}</option>)}
          </select>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', paddingBottom: 8 }}
          title="Se attivo, le ore di ogni giornata sono calcolate dalle 08:35 all'ultimo rapportino, invece che dal primo rapportino">
          <input type="checkbox" checked={inizioFisso} onChange={e => setInizioFisso(e.target.checked)} />
          Conta le ore dalle 08:35
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', paddingBottom: 8 }}
          title="Se attivo, i rapportini di sabato e domenica non vengono conteggiati">
          <input type="checkbox" checked={escludiWeekend} onChange={e => setEscludiWeekend(e.target.checked)} />
          Escludi sabati e domeniche
        </label>
        <button className="btn-primary" onClick={esporta} disabled={exporting || lista.length === 0}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Download size={16} /> {exporting ? 'Export...' : 'Stampa XLSX'}
        </button>
        <button className="btn-secondary" onClick={() => window.print()} disabled={lista.length === 0}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Printer size={16} /> Stampa
        </button>
      </div>

      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>
        Report ore tecnici — {MESI[mese - 1]} {anno}
      </h2>

      <div className="card" style={{ padding: '10px 14px', marginBottom: 16, display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13 }}>Ore da contratto</strong>
        <input type="number" className="input" min={1} max={60} step={0.5} value={oreSett}
          onChange={e => { const v = Number(e.target.value); if (v > 0) impostaOreSett(v) }}
          style={{ width: 80 }} />
        <span style={{ fontSize: 13, color: '#64748B' }}>ore a settimana</span>
        {[36, 40].map(v => (
          <button key={v} className={oreSett === v ? 'btn-primary' : 'btn-secondary'} onClick={() => impostaOreSett(v)}>{v}</button>
        ))}
        <span style={{ fontSize: 11, color: '#94A3B8' }}>({(oreSett / 5).toFixed(1).replace('.0', '')} h/giorno lun–ven)</span>
      </div>

      {inizioFisso && (
        <div style={{ fontSize: 12, color: '#92400e', background: '#fef3c7', borderRadius: 8, padding: '6px 10px', marginBottom: 12 }}>
          Ore calcolate dalle 08:35 all'ultimo rapportino della giornata.
        </div>
      )}

      {lista.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong style={{ fontSize: 14 }}>Andamento giornaliero — {MESI[mese - 1]} {anno}</strong>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className={metrica === 'ore' ? 'btn-primary' : 'btn-secondary'} onClick={() => setMetrica('ore')}>Ore</button>
              <button className={metrica === 'interventi' ? 'btn-primary' : 'btn-secondary'} onClick={() => setMetrica('interventi')}>Interventi</button>
            </div>
          </div>
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={datiGrafico} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.10)" />
                <XAxis dataKey="giorno" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} unit={metrica === 'ore' ? 'h' : ''} />
                <Tooltip formatter={(v: number) => metrica === 'ore' ? fmtOre(v) : v} labelFormatter={l => `Giorno ${l}`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {lista.map((t, i) => (
                  <Bar key={t.tecnico_id} dataKey={t.tecnico} fill={COLORI[i % COLORI.length]} radius={[3, 3, 0, 0]} isAnimationActive={false} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {isLoading && <div>Caricamento...</div>}
      {!isLoading && lista.length === 0 && <div className="card" style={{ padding: 20 }}>Nessun rapportino nel periodo selezionato.</div>}

      {lista.map(t => (
        <div key={t.tecnico_id} className="card" style={{ marginBottom: 16, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', background: '#1E40AF', color: '#fff', display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'baseline' }}>
            <strong style={{ fontSize: 15 }}>{t.tecnico}</strong>
            <span>{t.giorni_lavorati} giorni</span>
            <span>{t.totale_interventi} interventi</span>
            <span>{fmtOre(t.totale_ore)} totali</span>
            <span>media {fmtOre(t.media_ore_giorno)}/giorno</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f0f2f5', textAlign: 'left' }}>
                  <th style={{ padding: '8px 12px' }}>Data</th>
                  <th style={{ padding: '8px 12px' }}>Interventi</th>
                  <th style={{ padding: '8px 12px' }}>Primo</th>
                  <th style={{ padding: '8px 12px' }}>Ultimo</th>
                  <th style={{ padding: '8px 12px' }}>Ore lavorate</th>
                </tr>
              </thead>
              <tbody>
                {t.giorni.map(g => {
                  const k = `${t.tecnico_id}-${g.data}`
                  const open = aperti.has(k)
                  return (
                    <Fragment key={k}>
                      <tr onClick={() => toggle(k)} style={{ cursor: 'pointer', borderTop: '1px solid rgba(0,0,0,0.10)' }}>
                        <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                          <span className="no-print" style={{ verticalAlign: 'middle' }}>{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>{' '}
                          {g.giorno} {fmtData(g.data)}
                        </td>
                        <td style={{ padding: '8px 12px' }}>{g.n_interventi}</td>
                        <td style={{ padding: '8px 12px' }}>{g.primo}</td>
                        <td style={{ padding: '8px 12px' }}>{g.ultimo}</td>
                        <td style={{ padding: '8px 12px', fontWeight: 600 }}>{fmtOre(g.ore)}</td>
                      </tr>
                      {open && (
                        <tr className="stampa-det">
                          <td colSpan={5} style={{ padding: '4px 12px 12px 32px', background: '#fafbfc' }}>
                            <table style={{ width: '100%', fontSize: 12 }}>
                              <tbody>
                                {g.interventi.map(i => (
                                  <tr key={i.rapportino_id}>
                                    <td style={{ padding: '2px 8px', width: 60 }}>{i.ora}</td>
                                    <td style={{ padding: '2px 8px', width: 90 }}>{i.numero_pratica}</td>
                                    <td style={{ padding: '2px 8px' }}>{i.comune}</td>
                                    <td style={{ padding: '2px 8px' }}>{i.tipologia}</td>
                                    <td style={{ padding: '2px 8px', color: '#64748B', width: 90 }}>
                                      {i.minuti_dal_precedente != null ? `+${i.minuti_dal_precedente} min` : ''}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
          {(() => {
            const righe = calcolaSettimane(t, anno, mese, oreSett)
            const totPrev = righe.reduce((a, r) => a + r.previste, 0)
            const totFatte = righe.reduce((a, r) => a + r.fatte, 0)
            const totDiff = totFatte - totPrev
            return (
              <div style={{ borderTop: '2px solid rgba(0,0,0,0.10)', padding: '10px 16px 14px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>
                  Confronto con le ore da contratto ({oreSett} h/settimana)
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: '#64748B' }}>
                      <th style={{ padding: '4px 8px' }}>Settimana</th>
                      <th style={{ padding: '4px 8px' }}>Giorni lav.</th>
                      <th style={{ padding: '4px 8px' }}>Previste</th>
                      <th style={{ padding: '4px 8px' }}>Fatte</th>
                      <th style={{ padding: '4px 8px' }}>Differenza</th>
                    </tr>
                  </thead>
                  <tbody>
                    {righe.map(r => (
                      <tr key={r.label} style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                        <td style={{ padding: '4px 8px' }}>{r.label}</td>
                        <td style={{ padding: '4px 8px' }}>{r.giorniLav}</td>
                        <td style={{ padding: '4px 8px' }}>{fmtOre(r.previste)}</td>
                        <td style={{ padding: '4px 8px' }}>{fmtOre(r.fatte)}</td>
                        <td style={{ padding: '4px 8px', fontWeight: 600, color: coloreDiff(r.diff) }}>
                          {r.diff > 0.0083 ? 'ore in più ' : r.diff < -0.0083 ? 'ore in meno ' : ''}{fmtDiff(r.diff)}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: '2px solid rgba(0,0,0,0.15)', fontWeight: 700 }}>
                      <td style={{ padding: '6px 8px' }}>TOTALE MESE</td>
                      <td style={{ padding: '6px 8px' }}>{righe.reduce((a, r) => a + r.giorniLav, 0)}</td>
                      <td style={{ padding: '6px 8px' }}>{fmtOre(totPrev)}</td>
                      <td style={{ padding: '6px 8px' }}>{fmtOre(totFatte)}</td>
                      <td style={{ padding: '6px 8px', color: coloreDiff(totDiff) }}>{fmtDiff(totDiff)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )
          })()}
        </div>
      ))}
      <p className="no-print" style={{ fontSize: 11, color: '#94A3B8' }}>
        Ore = differenza tra l'orario del primo (o delle 08:35, se il flag è attivo) e dell'ultimo rapportino della giornata. Clicca su un giorno per vedere i singoli interventi. Le ore previste non escludono festività e ferie: i giorni senza rapportini contano come 0 ore fatte.
      </p>
    </div>
  )
}
